import { useState } from "react";
import { Link, NavLink, Outlet, useLocation } from "react-router-dom";
import { Menu, Phone, MapPin, Instagram, Facebook, Heart, X } from "lucide-react";
import { Sheet, SheetContent, SheetTrigger, SheetTitle } from "@/components/ui/sheet";
import { Button } from "@/components/ui/button";
import { LOGO_URL, BUSINESS, TAGLINE, PHONE, PHONE_LINK, CITY } from "@/lib/brand";

const NAV = [
  { to: "/", label: "Home" },
  { to: "/makeup-artists", label: "Makeup Artists" },
  { to: "/services", label: "Services" },
  { to: "/about", label: "About" },
  { to: "/contact", label: "Contact" },
];

export function Layout() {
  const [open, setOpen] = useState(false);
  const location = useLocation();
  const [inInstagram] = useState(() => typeof navigator !== "undefined" && /Instagram/i.test(navigator.userAgent));
  const [bannerDismissed, setBannerDismissed] = useState(false);

  return (
    <div className="min-h-screen bg-ivory flex flex-col">
      {inInstagram && !bannerDismissed && (
        <div data-testid="ig-browser-banner" className="bg-ink text-ivory text-xs sm:text-sm px-4 py-2.5 flex items-center justify-center gap-3 text-center">
          <span>For the best experience, open in your browser — tap <strong>⋮</strong> or <strong>Share</strong> → <strong>Open in Chrome / Safari</strong></span>
          <button data-testid="ig-banner-close" onClick={() => setBannerDismissed(true)} aria-label="Dismiss" className="shrink-0 p-1 text-ivory/60 hover:text-ivory">
            <X className="w-4 h-4" />
          </button>
        </div>
      )}
      <header className="sticky top-0 z-50 backdrop-blur-md bg-ivory/85 border-b border-[#EAE5DF]">
        <div className="px-4 sm:px-6 lg:px-12 max-w-7xl mx-auto flex items-center justify-between h-16 sm:h-20">
          <Link to="/" data-testid="nav-logo" className="flex items-center gap-2">
            <img src={LOGO_URL} alt={BUSINESS} className="h-12 sm:h-14 w-auto" />
          </Link>
          <nav className="hidden lg:flex items-center gap-8" data-testid="desktop-nav">
            {NAV.map((n) => (
              <NavLink
                key={n.to}
                to={n.to}
                data-testid={`nav-${n.label.toLowerCase().replace(/\s+/g, "-")}`}
                className={({ isActive }) =>
                  `text-sm tracking-wide transition-colors duration-200 ${
                    isActive ? "text-ink font-semibold" : "text-ink/60 hover:text-ink"
                  }`
                }
              >
                {n.label}
              </NavLink>
            ))}
          </nav>
          <div className="flex items-center gap-2 sm:gap-3">
            <a
              href={PHONE_LINK}
              data-testid="nav-call-link"
              className="hidden md:flex items-center gap-2 text-sm text-ink/70 hover:text-ink transition-colors"
            >
              <Phone className="w-4 h-4 text-velvet" /> {PHONE}
            </a>
            <Link to="/enquire" className="hidden sm:block">
              <Button
                data-testid="nav-enquire-btn"
                className="rounded-full bg-velvet hover:bg-velvethover text-ink font-semibold px-6 active:scale-95 transition-transform"
              >
                Enquire Now
              </Button>
            </Link>
            <Sheet open={open} onOpenChange={setOpen}>
              <SheetTrigger asChild>
                <button data-testid="mobile-menu-btn" className="lg:hidden p-2 text-ink" aria-label="Menu">
                  <Menu className="w-6 h-6" />
                </button>
              </SheetTrigger>
              <SheetContent side="right" className="bg-ivory w-[85vw]">
                <SheetTitle className="sr-only">Menu</SheetTitle>
                <div className="flex flex-col gap-1 mt-8">
                  {NAV.map((n) => (
                    <Link
                      key={n.to}
                      to={n.to}
                      data-testid={`mobile-nav-${n.label.toLowerCase().replace(/\s+/g, "-")}`}
                      onClick={() => setOpen(false)}
                      className={`py-3 px-2 font-serif text-2xl border-b border-[#EAE5DF] ${
                        location.pathname === n.to ? "text-velvet" : "text-ink"
                      }`}
                    >
                      {n.label}
                    </Link>
                  ))}
                  <Link to="/enquire" onClick={() => setOpen(false)} className="mt-6">
                    <Button data-testid="mobile-nav-enquire-btn" className="w-full rounded-full bg-velvet hover:bg-velvethover text-ink font-semibold h-12 text-base">
                      Book / Enquire
                    </Button>
                  </Link>
                  <a href={PHONE_LINK} data-testid="mobile-nav-call" className="mt-4 flex items-center gap-2 text-ink/70">
                    <Phone className="w-4 h-4 text-velvet" /> Call {PHONE}
                  </a>
                </div>
              </SheetContent>
            </Sheet>
          </div>
        </div>
      </header>

      <main className="flex-1">
        <Outlet />
      </main>

      <a
        href="https://wa.me/918318512668?text=Hi%20The%20Velvet%20Brush!%20I%27d%20like%20to%20enquire%20about%20a%20makeup%20booking."
        target="_blank"
        rel="noreferrer"
        data-testid="whatsapp-float"
        aria-label="Chat on WhatsApp"
        className="fixed bottom-5 right-5 z-[60] w-14 h-14 rounded-full bg-[#25D366] shadow-lg shadow-[#25D366]/40 flex items-center justify-center hover:scale-110 active:scale-95 transition-transform duration-200"
      >
        <svg viewBox="0 0 24 24" className="w-7 h-7 fill-white" xmlns="http://www.w3.org/2000/svg">
          <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
        </svg>
      </a>

      <footer className="bg-ink text-ivory mt-24">
        <div className="px-4 sm:px-6 lg:px-12 max-w-7xl mx-auto py-14 grid grid-cols-1 sm:grid-cols-3 gap-10">
          <div>
            <img src={LOGO_URL} alt={BUSINESS} className="h-14 w-auto bg-ivory rounded-lg p-1" />
            <p className="mt-4 text-xs tracking-[0.3em] text-blush">{TAGLINE}</p>
            <p className="mt-4 text-sm text-ivory/60 leading-relaxed">
              Premium makeup artistry for bridal, engagement and special occasions.
            </p>
          </div>
          <div>
            <h4 className="font-serif text-lg mb-4">Explore</h4>
            <div className="flex flex-col gap-2 text-sm text-ivory/70">
              {NAV.map((n) => (
                <Link key={n.to} to={n.to} data-testid={`footer-${n.label.toLowerCase().replace(/\s+/g, "-")}`} className="hover:text-blush transition-colors">
                  {n.label}
                </Link>
              ))}
              <Link to="/enquire" data-testid="footer-enquire" className="hover:text-blush transition-colors">Book / Enquire</Link>
            </div>
          </div>
          <div>
            <h4 className="font-serif text-lg mb-4">Contact</h4>
            <div className="flex flex-col gap-3 text-sm text-ivory/70">
              <a href={PHONE_LINK} data-testid="footer-call" className="flex items-center gap-2 hover:text-blush transition-colors">
                <Phone className="w-4 h-4 text-velvet" /> {PHONE}
              </a>
              <span className="flex items-center gap-2">
                <MapPin className="w-4 h-4 text-velvet" /> {CITY} | Available for Travel
              </span>
              <div className="flex gap-4 mt-2">
                <a href="https://instagram.com" target="_blank" rel="noreferrer" data-testid="footer-instagram" aria-label="Instagram" className="hover:text-blush transition-colors">
                  <Instagram className="w-5 h-5" />
                </a>
                <a href="https://facebook.com" target="_blank" rel="noreferrer" data-testid="footer-facebook" aria-label="Facebook" className="hover:text-blush transition-colors">
                  <Facebook className="w-5 h-5" />
                </a>
              </div>
            </div>
          </div>
        </div>
        <div className="border-t border-ivory/10">
          <div className="px-4 sm:px-6 lg:px-12 max-w-7xl mx-auto py-5 flex flex-col sm:flex-row justify-between gap-2 text-xs text-ivory/50">
            <span>© {new Date().getFullYear()} {BUSINESS}. All rights reserved.</span>
            <span className="flex items-center gap-1">
              Payment collected personally after booking confirmation <Heart className="w-3 h-3 text-velvet" />
            </span>
          </div>
        </div>
      </footer>
    </div>
  );
}
